// VideoStory.js - 重构后的主组件
import React, { useState, useEffect, useRef } from "react";
import { BookOpen } from "lucide-react";
import './VideoStory.css';
import { useSharedEvents } from "../TIMEBAR/SharedEventsContext";
import { useAppStore } from "../../store/appStore";
import EventNoteSystem from "./EventNoteSystem";

// 导入提取的钩子
import { 
  useVideoStoryEvents, 
  useObjectTracking, 
  useKeyboardNavigation,
  formatTime,
  getStoryDescription,
  createStoryTimeStamp
} from "./VideoStoryHooks";

// 导入提取的工具函数
import {
  eventLabels,
  getPriorityColor,
  getPriorityLabel,
  priorityOrder
} from "./VideoStoryUtils";

// 导入提取的组件
import {
  ScrollPreview,
  JumpMarker,
  KeyboardHint,
  StoryHeader,
  ScrollIndicator,
  EventItem,
  EmptyState,
  EventNavigation
} from "./VideoStoryComponents";

// 导入BboxDisplay（假设这是一个现有组件）
import BboxDisplay, { extractPersonFeatures, extractVehicleFeatures } from "../TIMEBAR/BboxDisplay";

// 获取事件图标组件
const getEventIcon = (type) => {
  const typeNum = Number(type);
  switch(typeNum) {
    case 1: return <img src="/smallicons/important_car_small.png" alt="Civil-Vehicle" width="18" height="18" />;
    case 2: return <img src="/smallicons/important_people_small.png" alt="Civil-Person" width="18" height="18" />;
    case 3: return <img src="/smallicons/important_others_small.png" alt="Civil-Other" width="18" height="18" />;
    case 4: return <img src="/smallicons/urgent_car_small.png" alt="Crime-Vehicle" width="18" height="18" />;
    case 5: return <img src="/smallicons/urgent_people_small.png" alt="Crime-Person" width="18" height="18" />;
    case 6: return <img src="/smallicons/urgent_others_small.png" alt="Crime-Other" width="18" height="18" />;
    default: return <BookOpen size={18} />;
  }
};

// VideoStory 主组件
const VideoStory = ({ 
  robotId, 
  onEventClick, 
  timeframe = "11AM", 
  activeEventId = null, 
  onTrackObject,
  onTogglePlayPause
}) => {
  // 使用提取的钩子
  const {
    events,
    eventTypes,
    trackedObjects,
    setTrackedObjects,
    storyProgress,
    storyTitle,
    animatingEventId,
    selectedEvent,
    sortMethod,
    setSortMethod,
    prepareGroupedEvents,
    handleEventActivation,
    handleEventClick,
    hasTrackedObjects,
    sortedEvents,
    isActiveEvent,
    effectiveTimeframe
  } = useVideoStoryEvents(robotId, timeframe, activeEventId, onEventClick);
  
  const { toggleTracking } = useObjectTracking(
    events, 
    trackedObjects, 
    setTrackedObjects, 
    robotId, 
    onTrackObject, 
    eventTypes, 
    getStoryDescription
  );
  
  const { showKeyboardHint } = useKeyboardNavigation(
    events, 
    selectedEvent, 
    handleEventActivation, 
    onTogglePlayPause
  );
  
  // 本地状态
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(true);
  const [previewInfo, setPreviewInfo] = useState({
    isVisible: false,
    eventId: null,
    position: 'middle' // 'top', 'middle', 'bottom'
  });
  const [jumpMarker, setJumpMarker] = useState({
    isVisible: false,
    position: { x: 0, y: 0 }
  });
  
  // Refs
  const storyContainerRef = useRef(null);
  
  // 获取共享事件上下文
  const { addSharedEvent } = useSharedEvents();
  
  // 切换头部展开/折叠状态
  const toggleHeader = () => {
    setIsHeaderExpanded(!isHeaderExpanded);
  };
  
  // 处理添加到其他区域
  const handleAddToOtherArea = (eventData) => {
    if (!events[eventData.eventId]) return;
    
    const eventToAdd = {
      ...events[eventData.eventId],
      notes: eventData.notes,
      id: eventData.eventId,
      robotId: robotId, // 添加机器人ID，确保唯一性
      timeframe: effectiveTimeframe // 记录时间帧
    };
    
    // 调用上下文中的添加函数
    addSharedEvent(eventToAdd);
  };
  
  // 创建自定义动画和样式
  useEffect(() => {
    // 创建样式元素
    const style = document.createElement('style');
    style.textContent = `
      @keyframes storyAppear {
        0% { opacity: 0; transform: translateY(20px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      
      @keyframes highlightPulse {
        0% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7); }
        50% { box-shadow: 0 0 0 8px rgba(59, 130, 246, 0.4); }
        100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
      }
      
      .story-chapter {
        animation: storyAppear 0.5s ease-out forwards;
        will-change: transform, opacity;
      }
      
      .story-highlight {
        animation: highlightPulse 2s ease-out;
        animation-iteration-count: 3;
        border-color: rgba(59, 130, 246, 0.8) !important;
        will-change: box-shadow, border-color;
      }
      
      .story-progress-indicator {
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 4px;
        background-color: #3b82f6;
        will-change: height;
      }
      
      .story-chapter-title {
        position: relative;
        transition: all 0.3s ease;
        will-change: transform;
      }
      
      .story-chapter-title:hover {
        transform: translateX(4px);
      }
      
      .story-image-container {
        overflow: hidden;
        border-radius: 0.5rem;
        transition: all 0.3s ease;
        will-change: transform;
      }
      
      .story-image-container:hover img {
        transform: scale(1.05);
      }
      
      .story-image {
        transition: transform 0.5s ease;
        will-change: transform;
      }

      /* 修改CSS，使内容始终显示而非折叠 */
      .story-chapter-content {
        overflow: visible !important;
        max-height: none !important;
        transition: none !important;
        padding: 0 1rem 1rem 1rem;
        margin-left: 2.5rem;
      }
    `;
    
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  return (
    <div className="story-container">
      {/* ScrollPreview - 滚动预览 */}
      <ScrollPreview 
        event={previewInfo.eventId !== null ? events[previewInfo.eventId] : null}
        isVisible={previewInfo.isVisible}
        position={previewInfo.position}
        timeframe={effectiveTimeframe}
        getEventIcon={getEventIcon}
        getPriorityColor={getPriorityColor}
        getPriorityLabel={getPriorityLabel}
        createStoryTimeStamp={createStoryTimeStamp}
      />
      
      {/* JumpMarker - 跳转标记 */}
      <JumpMarker 
        isVisible={jumpMarker.isVisible}
        position={jumpMarker.position}
      />
      
      {/* KeyboardHint - 键盘提示 */}
      <KeyboardHint isVisible={showKeyboardHint} />
      
      {/* StoryHeader - 故事标题和头部 */}
      <StoryHeader 
        storyTitle={storyTitle}
        storyProgress={storyProgress}
        isHeaderExpanded={isHeaderExpanded}
        toggleHeader={toggleHeader}
        events={events}
        groupedByPriority={prepareGroupedEvents()}
        priorityOrder={priorityOrder}
        getPriorityColor={getPriorityColor}
        getPriorityLabel={getPriorityLabel}
        handleEventActivation={handleEventActivation}
        activeEventId={selectedEvent}
        sortMethod={sortMethod}
        setSortMethod={setSortMethod}
        timeframe={effectiveTimeframe}
      />
      
      {/* 滚动内容容器 */}
      <div ref={storyContainerRef} className="story-chapters-container custom-scrollbar">
        {/* ScrollIndicator - 滚动指示器 */}
        <ScrollIndicator />
        
        {/* 章节内容 */}
        {events.length > 0 ? (
          sortedEvents.map((event, index) => {
            // 检查是否有边界框数据
            const hasBboxes = event.bboxes && Array.isArray(event.bboxes) && event.bboxes.length > 0;
            
            return (
              <EventItem 
                key={event.originalIndex}
                event={event}
                index={index}
                isSelected={selectedEvent === event.originalIndex}
                isActive={isActiveEvent(event.originalIndex)}
                animatingEventId={animatingEventId}
                getPriorityColor={getPriorityColor}
                eventLabels={eventLabels}
                getEventIcon={getEventIcon}
                getPriorityLabel={getPriorityLabel}
                createStoryTimeStamp={createStoryTimeStamp}
                timeframe={effectiveTimeframe}
                formatTime={formatTime}
                hasTrackedObjects={hasTrackedObjects}
                handleEventClick={handleEventClick}
                getStoryDescription={getStoryDescription}
                robotId={robotId}
                hasBboxes={hasBboxes}
                handleAddToOtherArea={handleAddToOtherArea}
                toggleTracking={toggleTracking}
                trackedObjects={trackedObjects}
                isGrouped={event.isGrouped}
                metadata={event.metadata}
              />
            );
          })
        ) : (
          <EmptyState />
        )}
      </div>
    </div>
  );
};

const arePropsEqual = (prevProps, nextProps) => {
  // Compare only the essential props that should trigger re-renders
  return (
    prevProps.robotId === nextProps.robotId &&
    prevProps.timeframe === nextProps.timeframe &&
    prevProps.activeEventId === nextProps.activeEventId
  );
};

export default React.memo(VideoStory, arePropsEqual);